import React from 'react'
import { useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import { Avatar } from '../../../components/ui/Avatar'
import { ActionButton } from './ActionButton'

import ReactMarkdown from 'react-markdown'
import rehypeSanitize from 'rehype-sanitize'
import rehypeRaw from 'rehype-raw'
import remarkGfm from 'remark-gfm'
import { AgentTooltipTrigger } from './AgentTooltipTrigger'
import { Copy, ThumbsUp, ThumbsDown, Check, FileText } from 'lucide-react'

import type { Message } from '../../../types/chat.types'
import type { Agent } from '../../../services/agentService'

interface ChatMessageProps {
    message: Message
    index: number
    agentName: string
    agentAvatar?: string | null
    agent?: Agent | null
    onCopy?: (content: string, index: number) => void
    onFeedback?: (index: number, feedback: 'liked' | 'disliked') => void

    copiedMessageId?: number | null
}

export const ChatMessage: React.FC<ChatMessageProps> = ({
    message,
    index,
    agentName,
    agentAvatar,
    agent,
    onCopy,
    onFeedback,

    copiedMessageId
}) => {
    const isUser = message.role === 'user'
    const isAssistant = message.role === 'assistant'
    const navigate = useNavigate()

    const handleProfileClick = (e: React.MouseEvent) => {
        e.stopPropagation()
        // Use agent handle/name if available, otherwise fallback to agentName prop
        const nameToUse = agent?.handle || agent?.name || agentName
        if (nameToUse) {
            const formattedHandles = nameToUse.toLowerCase().replace(/\s+/g, '')
            navigate(`/agentes/${formattedHandles}`)
        }
    }

    // Render user message
    if (isUser) {
        return (
            <motion.div
                {...({
                    initial: { opacity: 0, y: 20 },
                    animate: { opacity: 1, y: 0 },
                    exit: { opacity: 0 }
                } as any)}
                className="flex justify-end pl-12"
            >
                <div className="max-w-[80%]">
                    {/* Text content */}
                    {message.content && message.content.trim() && (
                        <div className="bg-[#eff3f4] dark:bg-gray-700 text-gray-900 dark:text-white rounded-[20px] rounded-tr-sm px-4 py-2">
                            <p className="text-sm whitespace-pre-wrap">{message.content}</p>
                        </div>
                    )}

                    {/* Attachments */}
                    {message.attachments && message.attachments.length > 0 && (
                        <div className={message.content && message.content.trim() ? "mt-2" : ""}>
                            {/* Images Grid */}
                            {message.attachments.some(att => att.type.startsWith('image/')) && (
                                <div className="flex flex-wrap gap-2 mb-2">
                                    {message.attachments.filter(att => att.type.startsWith('image/')).map((att, i) => (
                                        <div key={i} className="relative max-h-64 rounded-xl overflow-hidden border border-gray-200 dark:border-gray-700 bg-gray-100 dark:bg-gray-800">
                                            {att.data && (
                                                <img
                                                    src={att.data}
                                                    alt={att.name}
                                                    className="max-h-64 w-auto object-contain"
                                                    title={att.name}
                                                />
                                            )}
                                        </div>
                                    ))}
                                </div>
                            )}

                            {/* Other Files */}
                            {message.attachments.some(att => !att.type.startsWith('image/')) && (
                                <div className="space-y-2">
                                    {message.attachments.filter(att => !att.type.startsWith('image/')).map((att, i) => (
                                        <div key={i} className="flex items-center gap-3 bg-white/60 dark:bg-gray-800/60 rounded-lg p-2 border border-gray-200 dark:border-gray-700">
                                            <div className="flex-shrink-0 w-10 h-10 rounded overflow-hidden bg-gray-100 dark:bg-gray-700 flex items-center justify-center">
                                                <FileText className="w-5 h-5 text-gray-500 dark:text-gray-400" />
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <div className="text-xs font-medium text-gray-700 dark:text-gray-300 truncate">
                                                    {att.name}
                                                </div>
                                                {att.size && (
                                                    <div className="text-xs text-gray-500 dark:text-gray-400">
                                                        {(att.size / 1024).toFixed(1)} KB
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}

                    {/* Timestamp */}
                    {message.timestamp && (
                        <span className="text-xs text-gray-500 mt-1 block">
                            {message.timestamp}
                        </span>
                    )}
                </div>
            </motion.div>
        )
    }

    // Render assistant message
    if (isAssistant) {
        return (
            <motion.div
                {...({
                    initial: { opacity: 0, y: 20 },
                    animate: { opacity: 1, y: 0 },
                    exit: { opacity: 0 }
                } as any)}
                className="w-full pr-0"
            >
                <div className="flex gap-3">
                    {/* Avatar */}
                    <div className="flex-shrink-0 h-fit">
                        {agent ? (
                            <AgentTooltipTrigger agent={agent}>
                                <Avatar
                                    size="md"
                                    alt={agentName}
                                    src={agentAvatar || undefined}
                                    onClick={handleProfileClick}
                                    className="cursor-pointer"
                                />
                            </AgentTooltipTrigger>
                        ) : (
                            <Avatar
                                size="md"
                                alt={agentName}
                                src={agentAvatar || undefined}
                                onClick={handleProfileClick}
                                className="cursor-pointer"
                            />
                        )}
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                        {/* Header with name and timestamp */}
                        <div className="mb-1 relative">
                            {agent ? (
                                <AgentTooltipTrigger
                                    agent={agent}
                                    className="flex items-center gap-1 w-fit"
                                >
                                    <span
                                        className="font-bold text-gray-900 dark:text-white text-[15px] hover:underline cursor-pointer"
                                        onClick={handleProfileClick}
                                    >
                                        {agentName}
                                    </span>
                                    {/* Verified badge */}
                                    <svg
                                        className="w-[18px] h-[18px] cursor-pointer"
                                        viewBox="0 0 22 22"
                                        aria-label="Verified account"
                                        role="img"
                                        onClick={handleProfileClick}
                                    >
                                        <g>
                                            <path fill="#1d9bf0" d="M22.25 12c0-1.43-.88-2.67-2.19-3.34.46-1.39.2-2.9-.81-3.91s-2.52-1.27-3.91-.81c-.67-1.31-1.91-2.19-3.34-2.19s-2.67.88-3.33 2.19c-1.4-.46-2.91-.2-3.92.81s-1.26 2.52-.8 3.91c-1.31.67-2.2 1.91-2.2 3.34s.89 2.67 2.2 3.34c-.46 1.39-.21 2.9.8 3.91s2.52 1.26 3.91.81c.67 1.31 1.91 2.19 3.34 2.19s2.68-.88 3.34-2.19c1.39.45 2.9.2 3.91-.81s1.27-2.52.81-3.91c1.31-.67 2.19-1.91 2.19-3.34zm-11.71 4.2L6.8 12.46l1.41-1.42 2.26 2.26 4.8-5.23 1.47 1.32-6.2 6.81z"></path>
                                        </g>
                                    </svg>
                                </AgentTooltipTrigger>
                            ) : (
                                <div className="flex items-center gap-1 w-fit">
                                    <span
                                        className="font-bold text-gray-900 dark:text-white text-[15px] hover:underline cursor-pointer"
                                        onClick={handleProfileClick}
                                    >
                                        {agentName}
                                    </span>
                                    {/* Verified badge */}
                                    <svg
                                        className="w-[18px] h-[18px] cursor-pointer"
                                        viewBox="0 0 22 22"
                                        aria-label="Verified account"
                                        role="img"
                                        onClick={handleProfileClick}
                                    >
                                        <g>
                                            <path fill="#1d9bf0" d="M22.25 12c0-1.43-.88-2.67-2.19-3.34.46-1.39.2-2.9-.81-3.91s-2.52-1.27-3.91-.81c-.67-1.31-1.91-2.19-3.34-2.19s-2.67.88-3.33 2.19c-1.4-.46-2.91-.2-3.92.81s-1.26 2.52-.8 3.91c-1.31.67-2.2 1.91-2.2 3.34s.89 2.67 2.2 3.34c-.46 1.39-.21 2.9.8 3.91s2.52 1.26 3.91.81c.67 1.31 1.91 2.19 3.34 2.19s2.68-.88 3.34-2.19c1.39.45 2.9.2 3.91-.81s1.27-2.52.81-3.91c1.31-.67 2.19-1.91 2.19-3.34zm-11.71 4.2L6.8 12.46l1.41-1.42 2.26 2.26 4.8-5.23 1.47 1.32-6.2 6.81z"></path>
                                        </g>
                                    </svg>
                                </div>
                            )}
                        </div>

                        {/* Row 2: Handle and Time */}
                        <div className="flex items-center gap-2">
                            <span
                                className="text-gray-500 dark:text-gray-400 text-sm cursor-pointer hover:underline"
                                onClick={handleProfileClick}
                            >
                                @{agentName.toLowerCase().replace(/\s+/g, '')}
                            </span>
                            <span className="text-gray-500 dark:text-gray-400 text-sm hover:underline cursor-pointer">
                                {message.timestamp || (message.created_at ? new Date(message.created_at).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' }) : '')}
                            </span>
                        </div>

                        {/* Message content - NOW inside flex-1 */}
                        <div className="text-[14px] text-gray-900 dark:text-gray-100 leading-normal">
                            <div className="prose prose-sm max-w-none dark:prose-invert break-words">
                                <ReactMarkdown
                                    remarkPlugins={[remarkGfm]}
                                    rehypePlugins={[rehypeRaw, rehypeSanitize]}
                                    components={{
                                        a: ({ node, ...props }: any) => <a {...props} target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:underline" />,
                                        code: ({ node, className, children, ...props }: any) => {
                                            const match = /language-(\w+)/.exec(className || '')
                                            return match ? (
                                                <div className="relative group">
                                                    <code className={className} {...props}>
                                                        {children}
                                                    </code>
                                                </div>
                                            ) : (
                                                <code className="bg-gray-100 dark:bg-gray-800 rounded px-1 py-0.5" {...props}>
                                                    {children}
                                                </code>
                                            )
                                        }
                                    }}
                                >
                                    {message.content}
                                </ReactMarkdown>
                            </div>
                        </div>

                        {/* Action buttons */}
                        <div className="flex items-center gap-1 mt-2">

                            {onCopy && (
                                <ActionButton
                                    icon={copiedMessageId === index ? Check : Copy}
                                    label={copiedMessageId === index ? "Copiado!" : "Copiar"}
                                    onClick={() => onCopy(message.content, index)}
                                    isActive={copiedMessageId === index}
                                    activeColor="text-green-600"
                                    hoverColor="text-green-500"
                                />
                            )}
                            {onFeedback && (
                                <>
                                    <ActionButton
                                        icon={ThumbsUp}
                                        label="Gostei"
                                        onClick={() => onFeedback(index, 'liked')}
                                        isActive={message.feedback === 'liked'}
                                        hoverColor="text-pink-600"
                                    />
                                    <ActionButton
                                        icon={ThumbsDown}
                                        label="Não gostei"
                                        onClick={() => onFeedback(index, 'disliked')}
                                        isActive={message.feedback === 'disliked'}
                                        hoverColor="text-orange-500"
                                    />
                                </>
                            )}
                        </div>
                        {/* Close flex-1 */}
                    </div>
                    {/* Close flex gap-3 */}
                </div>
            </motion.div >
        )
    }

    // System message (fallback)
    return (
        <motion.div
            {...({
                initial: { opacity: 0 },
                animate: { opacity: 1 }
            } as any)}
            className="text-center text-sm text-gray-500 dark:text-gray-400"
        >
            {message.content}
        </motion.div>
    )
}
